import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  IconButton, 
  Typography, 
  Box, 
  Menu, 
  MenuItem, 
  ListItemIcon, 
  ListItemText,
  Avatar,
  Divider,
  Tooltip
} from '@mui/material';
import { 
  AccountCircle, 
  Logout, 
  Person, 
  AdminPanelSettings, 
  KeyboardArrowDown,
  Dashboard as DashboardIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { useSnackbar } from '../shared/contexts/SnackbarContext';
import { addThemeToUrl } from '../context/ThemeContext';

const AuthNavItem = () => {
  const navigate = useNavigate();
  const { user, logout, isAuthenticated, isAdmin, setSkipAuthCheck, setUser } = useAuth();
  const { showSnackbar } = useSnackbar();
  const [anchorEl, setAnchorEl] = useState(null);
  const [loggingOut, setLoggingOut] = useState(false);
  
  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    try {
      setLoggingOut(true);
      handleClose();
      
      console.log('🔓 [AuthNavItem] Starting logout process...');
      
      // Show success message immediately
      showSnackbar('Successfully logged out', 'success');
      
      // Clear user state and set skip flag immediately to prevent auth checks
      setSkipAuthCheck(true);
      // Clear user state to prevent ProtectedRoute from redirecting
      if (setUser) {
        setUser(null);
      }
      console.log('🔓 [AuthNavItem] Cleared user state and set skipAuthCheck');
      
      // Redirect immediately to prevent other components from redirecting to login
      console.log('🔓 [AuthNavItem] Redirecting to landing page immediately...');
      window.location.href = '/';
      
      // Don't call logout() API since it interferes with our skipAuthCheck flag
      // Just clear the cookie directly via API
      try {
        await fetch('/api/auth/logout', { method: 'POST', credentials: 'include' });
      } catch (error) {
        console.log('Background logout API error (ignored):', error);
      }
    } catch (error) {
      console.error('Logout error:', error);
      // Even if there's an error, still redirect to landing page
      console.log('🔓 [AuthNavItem] Error occurred, redirecting to landing page anyway...');
      window.location.href = '/';
    }
  };
  


  // Check if authenticated and has valid user
  if (!isAuthenticated() || !user) {
    return (
      <Tooltip title="Log in to your account">
        <IconButton 
          color="inherit" 
          onClick={() => {
            // Redirect to login page
            const isProduction = import.meta.env.PROD;
            const loginUrl = isProduction ? '/login' : 'http://localhost:3000/login';
            window.location.href = addThemeToUrl(loginUrl);
          }}
          disabled={loggingOut}
          size="small"
          sx={{ 
            bgcolor: 'rgba(255,255,255,0.1)', 
            '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' } 
          }}
        >
          <AccountCircle />
        </IconButton>
      </Tooltip>
    );
  }

  return (
    <div>
      <Tooltip title={`Signed in as ${user?.email || 'User'}`}>
        <Box
          onClick={handleMenu}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            cursor: 'pointer',
            bgcolor: 'rgba(255,255,255,0.1)',
            borderRadius: 2,
            px: 1,
            py: 0.5,
            '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' }
          }}
        >
                     <Avatar 
             src={user?.avatar || user?.avatarUrl || user?.profile?.avatarUrl}
             sx={{ 
               width: 26, 
               height: 26, 
               bgcolor: 'var(--color-primary)',
               fontSize: 'var(--font-size-md)',
               fontWeight: 500
             }}
           >
             {user?.email?.charAt(0).toUpperCase() || user?.username?.charAt(0).toUpperCase() || 'U'}
           </Avatar>
          
          <KeyboardArrowDown 
            fontSize="small" 
            sx={{ opacity: 0.7, width: 18, height: 18 }} 
          />
        </Box>
      </Tooltip>
      <Menu
        id="menu-appbar"
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        keepMounted
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        PaperProps={{
          elevation: 3,
          sx: { minWidth: 200 }
        }}
      >
        <MenuItem disabled>
          <ListItemIcon>
            {user?.role === 'super_admin' ? 
              <AdminPanelSettings fontSize="small" color="secondary" /> : 
              user?.role === 'admin' || user?.role === 'org_admin' ?
              <AdminPanelSettings fontSize="small" color="primary" /> :
              <Person fontSize="small" />}
          </ListItemIcon>
          <ListItemText 
            primary={user?.email || 'User'} 
            secondary={`Role: ${
              user?.role === 'super_admin' ? 'Super Admin' : 
              user?.role === 'admin' || user?.role === 'org_admin' ? 'Admin' :
              'User'
            }`} 
          />
        </MenuItem>
        
        <Divider />
        
        {/* Dashboard Navigation */}
        {(user?.organization_role === 'org_admin' || user?.organization?.slug || user?.is_admin) && (
          <MenuItem onClick={() => {
            // Super admin: go to global workspace default view
            if (user?.is_admin) {
              window.location.href = '/org/global/mmr';
              return;
            }
            // Prefer org dashboard for org admins or members
            if (user?.organization?.slug) {
              window.location.href = `/org/${user.organization.slug}`;
            } else {
              // org_admin without slug: go to org admin panel
              const isProduction = import.meta.env.PROD;
              window.location.href = isProduction ? '/admin/org' : 'http://localhost:3000/admin/org';
            }
          }}>
            <ListItemIcon>
              <DashboardIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="My Dashboard" />
          </MenuItem>
        )}
        
        {/* Admin Panel link for super admins and org admins */}
        {(user?.is_admin || user?.role === 'super_admin') && <MenuItem onClick={() => {
          // In development, use the full URL with port
          // In production, just use the /admin path on the same domain
          const isProduction = import.meta.env.PROD;
          const adminUrl = isProduction ? '/admin/' : 'http://localhost:3000/admin/';
          window.location.href = adminUrl;
        }}>
          <ListItemIcon>
            <AdminPanelSettings fontSize="small" color="secondary" />
          </ListItemIcon>
          <ListItemText primary="Super Admin" />
        </MenuItem>}
        
        {/* Fallback dashboard for users without org context */}
        {!(user?.is_admin) && !(user?.organization_role === 'org_admin') && !(user?.organization?.slug) && (
          <MenuItem onClick={() => {
            // No org context: send to global workspace default
            window.location.href = '/org/global/mmr';
          }}>
            <ListItemIcon>
              <DashboardIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="My Dashboard" />
          </MenuItem>
        )}
        
        <Divider />
        
        <MenuItem onClick={handleLogout} disabled={loggingOut}>
          <ListItemIcon>
            <Logout fontSize="small" color={loggingOut ? "disabled" : "inherit"} />
          </ListItemIcon>
          <ListItemText primary={loggingOut ? "Logging out..." : "Logout"} />
        </MenuItem>
      </Menu>
    </div>
  );
};

export default React.memo(AuthNavItem);
